import { useEffect, useState, useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { useDocument } from '@nandorojo/swr-firestore';
import {
  CollectionPath,
  SettingCollectionPath,
} from '@typings/collections.enum';
import { User } from '@typings/setting/userType.interface';
import {
  IExplorationPointSetting,
  initialExplorationPointSetting,
} from '@typings/setting/explorationSetting';

/**
 * 탐색 포인트 시스템
 *
 * 설계 원칙:
 * - Firestore를 단일 소스로 사용 (Recoil 중간 레이어 없음)
 * - 표시값은 저장된 포인트 + 경과 인터벌 수 × 회복량으로 즉시 계산 (별도 저장 없음)
 * - Firestore 쓰기는 (a) 차감 시, (b) 인터벌 완료 시에만 발생
 */
type UseExplorationPointOptions = {
  enabled?: boolean;
  enableTimer?: boolean;
};

export const useExplorationPoint = ({
  enabled = true,
  enableTimer = true,
}: UseExplorationPointOptions = {}) => {
  const currentUserID = useRecoilValue(currentUserIDState);
  const [timeUntilNext, setTimeUntilNext] = useState<number>(0);
  const shouldFetch = enabled;

  const { data: explorationSettings } = useDocument<IExplorationPointSetting>(
    shouldFetch ? `${SettingCollectionPath.EXPLORATION}` : null,
    { listen: true },
  );

  const { data: userData, set: setUserData } = useDocument<User>(
    shouldFetch && currentUserID
      ? `${CollectionPath.USERS}/${currentUserID}`
      : null,
    { listen: true },
  );

  const settings = explorationSettings || initialExplorationPointSetting;
  const INTERVAL_MS = settings.acquisitionInterval * 60 * 1000;
  const REWARD = settings.pointsPerInterval;
  const MAX = settings.maxPoints;

  /**
   * 표시 포인트: Firestore 저장값 + 경과 인터벌 수 × reward
   * Firestore에 쓰지 않고 렌더 시 즉시 계산
   */
  const explorationPoint = useMemo(() => {
    if (!shouldFetch || !settings.isActive || !userData) return 0;
    const stored = userData.explorationPoint ?? 0;
    const lastTime = userData.lastExplorationPointClaimTime ?? null;
    if (!lastTime || stored >= MAX) return Math.min(stored, MAX);
    const elapsed = Date.now() - lastTime;
    const earned = Math.min(
      Math.floor(elapsed / INTERVAL_MS) * REWARD,
      MAX - stored,
    );
    return stored + earned;
  }, [userData, settings.isActive, INTERVAL_MS, REWARD, MAX]);

  /**
   * 카운트다운 타이머 (UI 전용)
   * 인터벌 완료 시에만 Firestore에 1회 쓰기
   */
  useEffect(() => {
    if (!shouldFetch || !enableTimer || !settings.isActive || !userData) {
      setTimeUntilNext(0);
      return;
    }

    const tick = () => {
      const stored = userData.explorationPoint ?? 0;
      const lastTime = userData.lastExplorationPointClaimTime ?? null;

      if (!lastTime || explorationPoint >= MAX) {
        setTimeUntilNext(0);
        return;
      }

      const elapsed = Date.now() - lastTime;
      const intervalsElapsed = Math.floor(elapsed / INTERVAL_MS);
      const nextBase = lastTime + (intervalsElapsed + 1) * INTERVAL_MS;
      const remaining = nextBase - Date.now();
      setTimeUntilNext(Math.max(0, remaining));

      // 인터벌 완료 + MAX 미만인 경우에만 Firestore 업데이트
      if (remaining <= 0 && stored < MAX) {
        const newPoints = Math.min(
          stored + intervalsElapsed * REWARD,
          MAX,
        );
        const newLastClaimTime = lastTime + intervalsElapsed * INTERVAL_MS;
        setUserData?.(
          {
            explorationPoint: newPoints,
            lastExplorationPointClaimTime: newLastClaimTime,
          },
          { merge: true },
        );
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [
    shouldFetch,
    enableTimer,
    userData,
    settings.isActive,
    explorationPoint,
    MAX,
    INTERVAL_MS,
    REWARD,
    setUserData,
  ]);

  /**
   * 포인트 차감 (조사하기, 탐색 보드에서 사용)
   * 이미 획득된 인터벌을 반영한 lastClaimTime을 함께 저장해 이중 계산을 방지
   */
  const deductPoints = useCallback(
    async (amount: number): Promise<boolean> => {
      if (!shouldFetch || !userData || !setUserData || explorationPoint < amount) {
        return false;
      }

      const lastTime = userData.lastExplorationPointClaimTime ?? null;
      const elapsed = lastTime ? Date.now() - lastTime : 0;
      const intervalsElapsed = Math.floor(elapsed / INTERVAL_MS);
      const newLastClaimTime = lastTime
        ? lastTime + intervalsElapsed * INTERVAL_MS
        : Date.now();

      await setUserData(
        {
          explorationPoint: Math.max(0, explorationPoint - amount),
          lastExplorationPointClaimTime: newLastClaimTime,
        },
        { merge: true },
      );
      return true;
    },
    [shouldFetch, userData, setUserData, explorationPoint, INTERVAL_MS],
  );

  /**
   * 시간 포맷팅 (시:분:초)
   */
  const formatTimeUntilNext = useCallback(() => {
    if (!settings.isActive) return null;
    if (explorationPoint >= MAX) return null;
    if (timeUntilNext <= 0) return null;

    const hours = Math.floor(timeUntilNext / (1000 * 60 * 60));
    const minutes = Math.floor(
      (timeUntilNext % (1000 * 60 * 60)) / (1000 * 60),
    );
    const seconds = Math.floor((timeUntilNext % (1000 * 60)) / 1000);

    if (hours > 0) return `${hours}시간 ${minutes}분 ${seconds}초`;
    if (minutes > 0) return `${minutes}분 ${seconds}초`;
    return `${seconds}초`;
  }, [timeUntilNext, settings.isActive, explorationPoint, MAX]);

  return {
    explorationPoint,
    timeUntilNext,
    formatTimeUntilNext,
    isActive: settings.isActive,
    maxPoints: MAX,
    deductPoints,
    // 하위 호환성 유지 (기존 useExplorationPoints 사용처에서 점진적 마이그레이션)
    useExplorationPoints: deductPoints,
  };
};
